import { GridApi } from 'ag-grid-community';
import { groupBy } from 'lodash';
import moment, { Moment } from 'moment';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { PermissionSetType, User } from 'api/models';
import {
  TimesheetApprovalType,
  Configuration,
} from 'api/timesheets/configuration/configuration';
import { FormatTime } from 'components/organisms/timesheets/common/format-time';
import {
  BillabilitySummary,
  BodyWrapper,
  HeaderSummary,
  MetricSummary,
  Wrapper,
} from 'components/organisms/timesheets/common/style';
import { UtilizationTitle } from 'components/organisms/timesheets/common/summary';
import {
  calculateUtilization,
  getDays,
  getTotalBillableMinutes,
  getTotalMinutesFromEntries,
  getTotalNonBillableMinutes,
  getWeekPreset,
} from 'components/organisms/timesheets/common/utils';
import { getCapacityInHoursForADay } from 'components/organisms/user-capacity';
import { useUserPermissionSetType } from 'hooks/permissions';
import { getTimeOffDaysInAWeek } from 'utils/time-off-utils';
import { AllUsersTable } from '../all-users-view-table/all-users-table';
import { FreezeInfoNote } from '../my-timesheet';
import {
  TimesheetReminder,
  TimesheetStatus,
  TimesheetTableTimeEntry,
  UserLevelSummary,
  UserTimesheet,
  WeekPreset,
} from '../types';

const groupEntiresByDay = (entries: TimesheetTableTimeEntry[]) => {
  const groupedEntry = groupBy(entries, 'date');

  return Object.keys(groupedEntry).reduce((days, entryDate) => {
    return {
      ...days,
      [entryDate]: getTotalMinutesFromEntries(groupedEntry[entryDate]),
    };
  }, {});
};

export const getLatestReminderAt = (reminders: TimesheetReminder[]) => {
  if (!reminders) {
    return 0;
  }
  const sorted = reminders
    .map((reminder) => reminder.createdAt)
    .sort((a, b) => b - a);

  return sorted.length > 0 ? sorted[0] : 0;
};

const WeekPresetsAllowedInURL = [WeekPreset.ThisWeek];

const HeaderSummaryDetails = ({ userTimesheets, totalTimeoffHours }) => {
  const allEntries = userTimesheets.reduce(
    (entries, userTimesheet) =>
      userTimesheet.status === TimesheetStatus.NotSubmitted
        ? entries
        : entries.concat(userTimesheet.entries),
    [] as TimesheetTableTimeEntry[],
  );
  const capacity = userTimesheets.reduce(
    (totalCapacity, { user: { capacity: userCapacity } }) =>
      totalCapacity + (userCapacity ?? 0),
    0,
  );
  const totalMinutes = getTotalMinutesFromEntries(allEntries);
  const billableMinutes = getTotalBillableMinutes(allEntries);
  const nonBillableMinutes = getTotalNonBillableMinutes(allEntries);
  const utilization =
    userTimesheets.length === 0
      ? 0
      : calculateUtilization(
          billableMinutes,
          (capacity - totalTimeoffHours) * 60,
        );

  return (
    <HeaderSummary>
      <BillabilitySummary
        billableMinutes={billableMinutes}
        nonBillableMinutes={nonBillableMinutes}
        capacityInMinutes={capacity * 60}
      />
      <MetricSummary
        title={<FormattedMessage id="timesheets.summary.total_time_tracked" />}
        value={<FormatTime value={totalMinutes} showHypenIfZero={false} />}
      />
      <MetricSummary
        title={<FormattedMessage id="timesheets.summary.team_capacity" />}
        value={`${capacity - totalTimeoffHours}h`}
      />
      <MetricSummary
        title={<UtilizationTitle />}
        value={`${utilization.toFixed(1)}%`}
      />
    </HeaderSummary>
  );
};

interface Props {
  startDate: Moment;
  configuration: Configuration;
  userTimesheets: UserTimesheet[];
  approveTimesheet: (
    userId: number,
    updatedAt: number,
    projectId?: number,
  ) => void;
  sendReminder: (user: User) => void;
  refreshData: () => void;
  searchValue: string;
  isLoading: boolean;
  isManagerApproval: boolean;
  onGridReady: (params: any) => void;
  gridApi: GridApi | null;
}
export const TimesheetApprovalAllUserView: FC<Props> = ({
  startDate,
  configuration,
  userTimesheets,
  approveTimesheet,
  sendReminder,
  refreshData,
  searchValue,
  isLoading,
  isManagerApproval,
  onGridReady,
  gridApi,
}) => {
  const { push } = useHistory();

  const userPermissionType = useUserPermissionSetType();
  const isUserAdmin = userPermissionType === PermissionSetType.AccountAdmin;

  const isWeekFreezedAfter = useMemo(
    () => configuration.lockSetting.freeze.enableAfter,
    [configuration.lockSetting.freeze.enableAfter],
  );

  const isWeekFreezed = useMemo(() => {
    if (configuration.lockSetting.freeze.enabled) {
      const currentWeekDays = getDays(startDate, 7).map((x) =>
        x.format('YYYY-MM-DD'),
      );

      return (
        currentWeekDays.includes(
          configuration.lockSetting.freeze.enableAfter ?? '',
        ) || startDate < moment(configuration.lockSetting.freeze.enableAfter)
      );
    }

    return false;
  }, [
    configuration.lockSetting.freeze.enableAfter,
    configuration.lockSetting.freeze.enabled,
    startDate,
  ]);

  const userLevelSummaries: UserLevelSummary[] = useMemo(() => {
    return userTimesheets.map((userTimesheet) => {
      return {
        user: userTimesheet.user,
        userId: userTimesheet.user.userId,
        startDate: userTimesheet.startDate,
        status: userTimesheet.status,
        updatedAt: userTimesheet.updatedAt,
        lastReminderAt:
          userTimesheet.status === TimesheetStatus.NotSubmitted
            ? getLatestReminderAt(userTimesheet.reminders)
            : 0,
        days: groupEntiresByDay(userTimesheet.entries),
        projectId: userTimesheet.project?.id,
        projectName: userTimesheet.project?.name,
        reminders:
          userTimesheet.status === TimesheetStatus.NotSubmitted
            ? userTimesheet.reminders ?? []
            : [],
      };
    });
  }, [userTimesheets]);

  const totalTimeoffHours: number = userTimesheets.reduce(
    (acc: number, curr) => {
      let timeOffDays = 0;

      if (
        !!curr.user?.userTimeOffDetails &&
        curr.user?.userTimeOffDetails.length > 0
      ) {
        timeOffDays = getTimeOffDaysInAWeek(
          startDate,
          curr.user?.userTimeOffDetails,
        );
      }

      const timeOffHours =
        timeOffDays * getCapacityInHoursForADay(curr.user?.capacity);

      return acc + timeOffHours;
    },
    0,
  );

  const onUserSelection = (userId: number, projectId?: number) => {
    const pushToUrl = `/timesheets/${getWeekPreset(
      startDate,
      WeekPresetsAllowedInURL,
    )}/approvals/${userId}`;

    if (configuration.approvalSetting === TimesheetApprovalType.ProjectOwner) {
      push(`${pushToUrl}?projectId=${projectId}`);
    } else {
      push(pushToUrl);
    }
  };

  return (
    <Wrapper>
      {isManagerApproval && (
        <HeaderSummaryDetails
          userTimesheets={userTimesheets}
          totalTimeoffHours={totalTimeoffHours}
        />
      )}
      {isWeekFreezed && (
        <FreezeInfoNote isWeekFreezedAfter={isWeekFreezedAfter} allUserTable />
      )}
      <BodyWrapper>
        <AllUsersTable
          startDate={startDate}
          workingDays={configuration.workingDays}
          userTimesheets={userLevelSummaries}
          onUserSelection={onUserSelection}
          approveTimesheet={approveTimesheet}
          sendReminder={sendReminder}
          refreshData={refreshData}
          searchValue={searchValue}
          isWeekFreezed={isWeekFreezed}
          isLoading={isLoading}
          isManagerApproval={isManagerApproval}
          onGridReady={onGridReady}
          gridApi={gridApi}
          isUserAdmin={isUserAdmin}
        />
      </BodyWrapper>
    </Wrapper>
  );
};
